// src/admin/DataMigration/tools/StudentAttendanceTool.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, writeBatch, doc, query, orderBy } from 'firebase/firestore';
import { firestore } from '../../../firebaseConfig';

const StudentAttendanceTool = ({ setProgress, setStatus, setTerminalLog, isProcessing }) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAttYear, setSelectedAttYear] = useState('');
  const [attPreviewData, setAttPreviewData] = useState(null);
  const [showAttPreviewModal, setShowAttPreviewModal] = useState(false);
  const [isScanningAtt, setIsScanningAtt] = useState(false);

  useEffect(() => {
    const loadYears = async () => {
      try {
        const q = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.id }));
        setAcademicYears(list);
        if (list.length > 0) setSelectedAttYear(list[0].id);
      } catch (err) {
        console.error("Lỗi tải danh sách niên khóa:", err);
      }
    };
    loadYears();
  }, []);

  const handleScanAttendancePreview = async () => {
    if (!selectedAttYear) {
      alert('Vui lòng chọn Niên khóa cần chuyển đổi điểm danh.');
      return;
    }

    try {
      setIsScanningAtt(true);
      setTerminalLog(`🔍 Đang quét dữ liệu điểm danh từ academic_years/${selectedAttYear}/attendance...\n`);

      const attSnap = await getDocs(collection(firestore, `academic_years/${selectedAttYear}/attendance`));
      if (attSnap.empty) {
        alert(`Không tìm thấy dữ liệu điểm danh nào trong academic_years/${selectedAttYear}/attendance.`);
        setIsScanningAtt(false);
        return;
      }

      const studentsMap = {};
      let totalDocsRead = 0;
      let totalLogsParsed = 0;

      attSnap.docs.forEach((docSnap) => {
        const docId = docSnap.id;
        const data = docSnap.data();
        totalDocsRead++;

        let attType = '';
        let dateStr = '';

        if (docId.endsWith('_church')) {
          attType = 'church';
          dateStr = docId.replace('_church', '');
        } else if (docId.endsWith('_catechism')) {
          attType = 'catechism';
          dateStr = docId.replace('_catechism', '');
        } else {
          return;
        }

        const logsMap = data.logs || data;

        Object.entries(logsMap).forEach(([sId, logValue]) => {
          if (typeof logValue === 'object' && logValue !== null && logValue.time) {
            if (!studentsMap[sId]) {
              studentsMap[sId] = { church: {}, catechism: {} };
            }
            studentsMap[sId][attType][dateStr] = logValue;
            totalLogsParsed++;
          }
        });
      });

      const totalStudentsFound = Object.keys(studentsMap).length;

      setAttPreviewData({
        yearId: selectedAttYear,
        studentsMap,
        stats: {
          totalDocsRead,
          totalStudentsFound,
          totalLogsParsed
        }
      });

      setTerminalLog(prev => prev + `✓ Đã quét xong ${totalDocsRead} file ngày.\n✓ Phát hiện ${totalStudentsFound} thiếu nhi với tổng ${totalLogsParsed} lượt điểm danh.\n`);
      setShowAttPreviewModal(true);
    } catch (err) {
      console.error('Lỗi khi quét xem trước điểm danh:', err);
      alert('Lỗi khi quét dữ liệu: ' + err.message);
    } finally {
      setIsScanningAtt(false);
    }
  };

  const handleConfirmAttendanceMigration = async () => {
    if (!attPreviewData) return;

    setShowAttPreviewModal(false);
    setStatus('processing');
    setProgress(1);
    setTerminalLog(`🚀 Bắt đầu lưu dữ liệu điểm danh mới vào academic_years/${attPreviewData.yearId}/student_attendances...\n`);

    try {
      const { yearId, studentsMap } = attPreviewData;
      const studentIds = Object.keys(studentsMap);
      const totalStudents = studentIds.length;
      const CHUNK_SIZE = 50; 
      let totalMigratedStudents = 0;

      for (let i = 0; i < totalStudents; i += CHUNK_SIZE) {
        const chunk = studentIds.slice(i, i + CHUNK_SIZE);
        let batch = writeBatch(firestore);

        chunk.forEach((sId) => {
          const studentAttObj = studentsMap[sId];
          const targetRef = doc(firestore, `academic_years/${yearId}/student_attendances`, sId);
          
          batch.set(targetRef, {
            church: studentAttObj.church || {},
            catechism: studentAttObj.catechism || {},
            updatedAt: new Date().toISOString()
          }, { merge: true });
        });

        await batch.commit();
        totalMigratedStudents += chunk.length;
        const currentPct = Math.round((totalMigratedStudents / totalStudents) * 100);
        
        setProgress(currentPct);
        setTerminalLog(prev => prev + `  + [Đã lưu lô]: ${totalMigratedStudents}/${totalStudents} thiếu nhi (${currentPct}%)\n`);
      }

      setProgress(100);
      setStatus('done');
      setTerminalLog(prev => prev + `\n🎉 TỔNG KẾT: Đã di trú điểm danh thành công cho toàn bộ ${totalMigratedStudents} thiếu nhi!\n`);
    } catch (err) {
      console.error('Lỗi khi lưu điểm danh mới:', err);
      setStatus('error');
      setTerminalLog(prev => prev + `❌ LỖI LƯU ĐIỂM DANH: ${err.message}\n`);
    }
  };

  return (
    <div className="dmm-att-section-card">
      <div className="dmm-att-controls">
        <div className="dmm-select-group">
          <label>Chọn Niên khóa cần chuyển đổi điểm danh:</label>
          <select 
            value={selectedAttYear} 
            onChange={(e) => setSelectedAttYear(e.target.value)}
            className="dmm-year-select"
            disabled={isProcessing || isScanningAtt}
          >
            {academicYears.map(y => (
              <option key={y.id} value={y.id}>{y.name} ({y.id})</option>
            ))}
          </select>
        </div>

        <button
          onClick={handleScanAttendancePreview}
          disabled={isProcessing || isScanningAtt}
          className="dmm-execute-btn"
        >
          {isScanningAtt ? '⏳ Đang quét...' : '🔍 Quét & Xem Trước Dữ Liệu'}
        </button>
      </div>

      {showAttPreviewModal && attPreviewData && (
        <div className="dmm-modal-backdrop">
          <div className="dmm-modal-card">
            <div className="dmm-modal-header">
              <h3>⚡ BẢN XEM TRƯỚC DI TRÚ ĐIỂM DANH</h3>
              <button className="dmm-modal-close-btn" onClick={() => setShowAttPreviewModal(false)}>✕</button>
            </div>

            <div className="dmm-modal-body">
              <div className="dmm-stats-grid">
                <div className="dmm-stat-card">
                  <span className="stat-label">File Ngày Quét</span>
                  <span className="stat-value">{attPreviewData.stats.totalDocsRead}</span>
                </div>
                <div className="dmm-stat-card highlight">
                  <span className="stat-label">Thiếu Nhi Được Chuyển</span>
                  <span className="stat-value">{attPreviewData.stats.totalStudentsFound}</span>
                </div>
                <div className="dmm-stat-card">
                  <span className="stat-label">Tổng Lượt Điểm Danh</span>
                  <span className="stat-value">{attPreviewData.stats.totalLogsParsed}</span>
                </div>
              </div>

              <h4 className="dmm-table-title">Chi Tiết Lượt Điểm Danh (50 em đầu)</h4>
              <div className="dmm-preview-table-wrapper">
                <table className="dmm-preview-table">
                  <thead>
                    <tr>
                      <th>STT</th>
                      <th>Mã ID Thiếu Nhi</th>
                      <th>Số Ngày Đi Lễ</th>
                      <th>Số Ngày Học Giáo Lý</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(attPreviewData.studentsMap).slice(0, 50).map(([sId, attObj], idx) => (
                      <tr key={sId}>
                        <td>{idx + 1}</td>
                        <td className="td-code">{sId}</td>
                        <td><span className="badge-church">{Object.keys(attObj.church || {}).length} ngày</span></td>
                        <td><span className="badge-catechism">{Object.keys(attObj.catechism || {}).length} ngày</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dmm-modal-footer">
              <button onClick={() => setShowAttPreviewModal(false)} className="dmm-btn-cancel">✕ Hủy Bỏ</button>
              <button onClick={handleConfirmAttendanceMigration} className="dmm-btn-confirm">
                🚀 Xác Nhận Lưu Vào Firestore
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentAttendanceTool;